import {ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FintechService} from "../../../services/fintech.service";
import {FintechPayMethodInfoComponent} from "./fintech-pay-method-info/fintech-pay-method-info.component";
import {MatDialog} from "@angular/material/dialog";
import {NgxFlickingComponent} from "@egjs/ngx-flicking";
import {Subscription} from "rxjs";
import {DialogPopComponent} from "../../../components/dialog-pop/dialog-pop.component";
import {FintechAddStripeComponent} from "./fintech-add-stripe/fintech-add-stripe.component";
import {FintechAddBraintreeComponent} from "./fintech-add-braintree/fintech-add-braintree.component";

type FintechModes = 'ACCOUNT' | 'PURCHASE' | 'WIZARD';

@Component({
    selector: 'app-fintech-paymethod-editor',
    templateUrl: './fintech-pay-method-editor.component.html',
    styleUrl: './fintech-pay-method-editor.component.scss'
})
export class FintechPayMethodEditorComponent implements OnInit, OnDestroy {
    @ViewChild('subscriptionPayMethod') subscriptionPayMethod!: FintechPayMethodInfoComponent;
    @ViewChild('purchasesPayMethod') purchasesPayMethod!: FintechPayMethodInfoComponent;
    @ViewChild('flk') flk!: NgxFlickingComponent;

    private subscription: Subscription = new Subscription();

    flickingOptions = {
        align: 'prev',
        horizontal: true
    };


    payMethodList;

    cardInfoLabel;

    isFlickingVisible = false;

    recurringPayMethodId;
    orderPayMethodId;

    selectionFor = "Subscription";

    fintechMode;

    @Input()
    set setFintechMode(i_fintechMode: FintechModes) {
        this.fintechMode = i_fintechMode;
    }


    constructor(public fintechService: FintechService,
                private dialog: MatDialog,
                private changeDetectorRef: ChangeDetectorRef) {
    }

    ngOnInit(): void {
        this.subscription.add(
            this.fintechService.notify$.subscribe(msg => {
                if (this.fintechService.data.payMethodList != null) {
                    this.payMethodList = this.fintechService.data.payMethodList;
                    this.recurringPayMethodId = this.fintechService.data.recurringPayMethodId;
                    this.orderPayMethodId = this.fintechService.data.orderPayMethodId;
                    this.changeDetectorRef.detectChanges();
                    this.updatePayMethodInfo();
                }
            })
        );

        // Force refresh of fintech data on component load
        this.refreshFintechData();
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    private refreshFintechData(): void {
        // Clear the cache to force fresh data
        this.fintechService.fintechInfoCache$ = null;

        this.subscription.add(
            this.fintechService.getFintechInfo().subscribe({
                next: () => {
                    // Data will be updated via notify$ subscription
                },
                error: (error) => {
                    console.error('Failed to refresh fintech data:', error);
                }
            })
        );
    }

    showRecurringCard() {
        switch (this.fintechMode) {
            case "ACCOUNT":
                return this.fintechService.recurringMode != -1;

            case "PURCHASE":
                return false;

            case "WIZARD":
                return true;
        }
    }

    showPurchasesCard() {
        switch (this.fintechMode) {
            case "ACCOUNT":
                return true;

            case "PURCHASE":
                return true;

            case "WIZARD":
                return false;
        }
    }

    updatePayMethodInfo() {
        if (this.subscriptionPayMethod != null) {
            this.subscriptionPayMethod.payMethodInfo = null;
        }
        if (this.purchasesPayMethod != null) {
            this.purchasesPayMethod.payMethodInfo = null;
        }

        this.payMethodList.forEach(payMethodInfo => {
            if (!payMethodInfo) return;
            if (this.subscriptionPayMethod != null) {
                if (payMethodInfo.payMethodId == this.recurringPayMethodId) {
                    this.subscriptionPayMethod.payMethodInfo = payMethodInfo;
                }
            }

            if (this.purchasesPayMethod != null) {
                if (payMethodInfo.payMethodId == this.orderPayMethodId) {
                    this.purchasesPayMethod.payMethodInfo = payMethodInfo;
                }
            }
        });
    }

    onReady() {
        if (this.payMethodList.length > 0) {
            this.flk.moveTo(0);
            this.isFlickingVisible = true;
        }
    }

    canSelectPayMethod() {
        return (this.recurringPayMethodId != this.fintechService.data.recurringPayMethodId || this.orderPayMethodId != this.fintechService.data.orderPayMethodId);
    }

    onReplaceCard(selectionFor) {
        this.selectionFor = selectionFor;
        this.fintechService.editMode = "billing";
    }


    onAddPayMethod() {
        const dialogConfig = {
            width: '90vw',
            maxWidth: '600px',
            maxHeight: '90vh',
            autoFocus: true
        };
        const dialogRef = (this.fintechService.defaultFintech == "Stripe")
            ? this.dialog.open(FintechAddStripeComponent, dialogConfig)
            : this.dialog.open(FintechAddBraintreeComponent, dialogConfig);
        dialogRef.afterClosed().subscribe(payMethodInfo => {
            if (payMethodInfo != null) {
                this.payMethodList.push(payMethodInfo);
                this.recurringPayMethodId = this.fintechService.data.recurringPayMethodId;
                this.orderPayMethodId = this.fintechService.data.orderPayMethodId;
                this.isFlickingVisible = true;
            }
        });
    }

    onPayMethodCancel() {
        this.recurringPayMethodId = this.fintechService.data.recurringPayMethodId;
        this.orderPayMethodId = this.fintechService.data.orderPayMethodId;
        this.fintechService.editMode = "info";
        this.isFlickingVisible = false;
        this.changeDetectorRef.detectChanges();
        this.updatePayMethodInfo();
    }

    onPayMethodUpdate() {
        this.fintechService.selectPayMethod(this.recurringPayMethodId, this.orderPayMethodId).subscribe(res => {
            this.fintechService.editMode = "info";
            this.isFlickingVisible = false;
            this.changeDetectorRef.detectChanges();
            this.updatePayMethodInfo();
        });
    }

    onPayMethodDelete(payMethodId) {
        if (this.selectionFor == "Purchases" && payMethodId == this.recurringPayMethodId && this.showRecurringCard()) {
            const dialogRef = this.dialog.open(DialogPopComponent, {
                height: '200px',
                width: '400px',
                data: {name: 'this payment method is used by the recurring payments and can not be removed.', showCancel: false}
            });
        } else {
            const dialogRef = this.dialog.open(DialogPopComponent, {
                height: '200px',
                width: '400px',
                data: {name: 'are you sure you want remove this payment method yes/no?', showCancel: true}
            });
            dialogRef.afterClosed().subscribe((r) => {
                if (r) {
                    this.fintechService.deletePayMethod(payMethodId).subscribe(res => {
                        this.payMethodList = this.payMethodList.filter(item => item.payMethodId !== payMethodId);
                        this.fintechService.data.recurringPayMethodId = this.recurringPayMethodId = res.recurringPayMethodId;
                        this.fintechService.data.orderPayMethodId = this.orderPayMethodId = res.orderPayMethodId;
                    });
                }
            });
        }
    }
}

